import { useState } from "react";
import AppHeader from "@/components/AppHeader";
import { cations, anions, getSolubility, type Solubility } from "@/data/ions";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

const solubilityColors: Record<Solubility, string> = {
  S: "bg-primary/20 text-primary",
  I: "bg-destructive/15 text-destructive",
  sS: "bg-cat-transition/20 text-foreground",
};

const solubilityLabels: Record<Solubility, string> = {
  S: "Soluble",
  I: "Insoluble",
  sS: "Slightly",
};

export default function SolubilityTable() {
  const [hoveredCell, setHoveredCell] = useState<{ cat: number; an: number } | null>(null);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Solubility Rules Table — ElementPad" description="Interactive solubility table for common ionic compounds. Look up whether a salt dissolves in water with quick filters and notes." path="/tools/solubility" />
      <AppHeader />
      <Main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Solubility Reference Table</h1>
        <p className="text-sm text-muted-foreground mb-4">
          Solubility of common ionic compounds in water at room temperature.
        </p>

        {/* Legend */}
        <div className="flex gap-4 mb-6">
          {(["S", "I", "sS"] as Solubility[]).map((s) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded text-xs font-mono font-bold flex items-center justify-center ${solubilityColors[s]}`}>
                {s}
              </span>
              <span className="text-sm text-muted-foreground">{solubilityLabels[s]}</span>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="text-xs" aria-label="Solubility of ionic compounds - cations vs anions">
            <caption className="sr-only">Solubility table showing whether combinations of cations and anions are soluble (S), insoluble (I), or slightly soluble (sS) in water</caption>
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-muted p-2 text-left text-muted-foreground font-medium min-w-[100px]">
                  Cation \ Anion
                </th>
                {anions.map((an, j) => (
                  <th
                    key={j}
                    className={`p-2 text-center font-mono font-medium min-w-[60px] transition-colors ${
                      hoveredCell?.an === j ? "bg-accent text-foreground" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    <div className="whitespace-nowrap">{an.formula}</div>
                    <div className="font-sans text-[10px] font-normal opacity-70">{an.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cations.map((cat, i) => (
                <tr key={i} className="border-t border-border">
                  <td
                    className={`sticky left-0 z-10 p-2 font-mono font-medium transition-colors ${
                      hoveredCell?.cat === i ? "bg-accent text-foreground" : "bg-tile-bg text-foreground"
                    }`}
                  >
                    <div>{cat.formula}</div>
                    <div className="font-sans text-[10px] font-normal text-muted-foreground">{cat.name}</div>
                  </td>
                  {anions.map((an, j) => {
                    const sol = getSolubility(cat, an);
                    return (
                      <td
                        key={j}
                        className={`p-2 text-center font-mono font-bold transition-colors cursor-default ${solubilityColors[sol]} ${
                          hoveredCell?.cat === i || hoveredCell?.an === j ? "ring-1 ring-inset ring-border" : ""
                        }`}
                        onMouseEnter={() => setHoveredCell({ cat: i, an: j })}
                        onMouseLeave={() => setHoveredCell(null)}
                        title={`${cat.name} + ${an.name}: ${solubilityLabels[sol]}`}
                      >
                        {sol}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Main>
    </div>
  );
}
